import { createStore, compose, applyMiddleware } from 'redux';
import { Provider } from 'react-redux';
// @ts-expect-error untyped dependency
import { DragDropContextProvider } from 'react-dnd';
import thunk from 'redux-thunk';
import { ApolloProvider } from '@apollo/client';
import { createApolloClient } from 'hubspot-apollo-client';
import { contains } from 'underscore';
import { AppProvider } from 'foundations-components/components/AppProvider';
import { UsageTrackingProvider } from 'vis-exp-editor-common/tracking/useUsageTracking';
import { renderWithNavCopilot } from 'quartz-core/client-with-copilot';

// @ts-expect-error untyped dependency
import HTML5Backend from 'react-dnd-html5-backend';
import routerMiddleware from 'FormsUI/middlewares/routerMiddleware';
import createHistory from 'FormsUI/utils/createHistory';
import userInfo from 'hub-http/userInfo';
// @ts-expect-error its fine
import NotificationMiddleware from 'ReduxMessenger/middleware/NotificationMiddleware';
import OnboardingTour from 'onboarding-tours/components/OnboardingTour';

//I18n Configuration
import createStandardI18nProvider from 'I18n/init/providers/createStandardI18nProvider';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import registerAllData from 'I18n/init/register/sync/registerAllData';

// Import extra I18n data
import 'I18n/init/data/currency';
import 'I18n/init/data/baseLocales';
import 'I18n/init/data/publicLocales';

// Rhumb
import { RhumbProvider } from 'react-rhumb';
import rhumbConfig from '../rhumb-config.yaml';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import createAllI18nProvider from 'I18n/init/providers/createAllI18nProvider';
import extendedLang from 'i2l?very-lazy&query=sporks!../lang/extended/en.lyaml';
import { FormsAILibProvider } from 'forms-ai-lib/providers/FormsAILibProvider';

// @ts-expect-error no types
import RootReducer from 'FormsUI/reducers/Root';
import { setUser } from 'FormsUI/actions/User';
import { setGates } from 'FormsUI/actions/Gates';
import { setPortal } from 'FormsUI/actions/Portal';
import Router from 'FormsUI/Router';
// @ts-expect-error no types
import EventBus from 'FormsUI/lib/EventBus';
import UsageTracker from 'FormsUI/utils/UsageTracker';
// @ts-expect-error no types
import postMessageMiddleware from 'FormsUI/middlewares/postMessageMiddleware';
import usageTrackerMiddleware from 'forms-utils-lib/middleware/usageTrackerMiddleware';
import Routes from 'ContentUtils/Routes';
import { FORMS_READ } from 'FormsUI/constants/Scopes';
import { getCoreDashboardRoutePrefix } from 'FormsUI/lib/RouteGenerators';
import { captureException } from 'raven-js';
import { fetchInventory } from 'FormsUI/actions/inventory';
import { getIsStarterPortal } from 'onboarding-scopes';
import { fetchDataPrivacySettings } from 'forms-utils-lib/dataPrivacySettings/actions/DataPrivacySettings';
// @ts-expect-error no types
import { getDataPrivacySettingsKey } from 'FormsUI/earlyRequest/earlyRequestsShared';
import PageStatusChangeTrackingWrapper from 'FormsUI/components/PageStatusChangeTrackingWrapper';
import FormRoutingProvider from 'FormsUI/components/editor/options/routing/FormRoutingProvider';
import UIApplication from 'UIComponents/app/UIApplication';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { initializeAuth } from 'FormsUI/actions/AppActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function trackUsage(eventName, eventProps) {
  UsageTracker.track(eventName, eventProps);
}
const apolloClient = createApolloClient();
export default class FormsApp {
  bootstrap() {
    const I18nProvider = createStandardI18nProvider();
    const langAvailable = Promise.all([I18nProvider.register(lang), registerAllData(I18nProvider)]);

    // These should be moved to where they're needed and code split
    const AllI18nProvider = createAllI18nProvider();
    AllI18nProvider.register(extendedLang).catch(captureException);
    const userInfoPromise = this.getUserInfo(I18nProvider);
    Promise.all([userInfoPromise, langAvailable]).then(([userData]) => {
      const {
        user,
        gates,
        portal
      } = userData;
      // @ts-expect-error its fine
      this.history = createHistory();
      // @ts-expect-error its fine
      this.store = this.createStore({
        history: this.history
      });
      // @ts-expect-error its fine
      EventBus.setStore(this.store);
      // @ts-expect-error its fine
      this.dataFetchingClient = new DataFetchingClient();
      this.handleUserData(user);
      this.handleGateData(gates);
      this.handlePortalData(portal);
      this.handleAuthData(userData);

      // This check is to reduce the volume of network requests to the
      // Inventory API, as we are only interested in Inventory for
      // Starter Portals
      if (getIsStarterPortal(user.scopes)) {
        // @ts-expect-error its fine
        this.store.dispatch(fetchInventory());
      }
      // @ts-expect-error its fine
      this.store.dispatch(fetchDataPrivacySettings({
        earlyRequestKey: getDataPrivacySettingsKey()
      }));
      this.mountApp();
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  createStore({
    history
  }) {
    // @ts-expect-error its fine
    const devTools = window.__REDUX_DEVTOOLS_EXTENSION__ ?
    // @ts-expect-error its fine
    window.__REDUX_DEVTOOLS_EXTENSION__() : args => args;
    return createStore(RootReducer, compose(applyMiddleware(thunk, routerMiddleware(history), NotificationMiddleware, postMessageMiddleware(EventBus), usageTrackerMiddleware(trackUsage)), devTools));
  }
  getUserInfo(I18nProvider) {
    return userInfo().then(({
      gates,
      user,
      portal
    }) => {
      I18nProvider.setLocale({
        locale: user.locale,
        langEnabled: user.lang_enabled,
        timezone: portal.timezone
      });
      if (!contains(user.scopes, FORMS_READ)) {
        // @ts-expect-error its fine
        window.location = getCoreDashboardRoutePrefix();
      }
      return {
        gates,
        user,
        portal
      };
    });
  }
  handleAuthData(auth) {
    // @ts-expect-error its fine
    this.store.dispatch(initializeAuth(auth));
  }
  handleUserData(user) {
    // @ts-expect-error its fine
    this.store.dispatch(setUser(user));
  }
  handleGateData(gates) {
    Routes.init(gates);
    // @ts-expect-error its fine
    this.store.dispatch(setGates(gates));
  }
  handlePortalData(portal) {
    // @ts-expect-error its fine
    this.store.dispatch(setPortal(portal));
  }
  mountApp() {
    renderWithNavCopilot( /*#__PURE__*/_jsx(UIApplication, {
      name: "FormsUI",
      children: /*#__PURE__*/_jsx(AppProvider, {
        children: /*#__PURE__*/_jsx(UsageTrackingProvider, {
          appKey: "trackEditorInteraction",
          usageTracker: UsageTracker,
          children: /*#__PURE__*/_jsx(ApolloProvider, {
            client: apolloClient,
            children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
              client: this.dataFetchingClient,
              children: /*#__PURE__*/_jsx(FormsAILibProvider, {
                children: /*#__PURE__*/_jsxs(Provider, {
                  store: this.store,
                  children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(DragDropContextProvider, {
                    backend: HTML5Backend,
                    children: /*#__PURE__*/_jsxs(_Fragment, {
                      children: [/*#__PURE__*/_jsx(OnboardingTour, {}), /*#__PURE__*/_jsx(RhumbProvider
                      /* @ts-expect-error its fine */, {
                        config: rhumbConfig
                        /* @ts-expect-error its fine */,
                        history: historyAdapter(this.history),
                        children: /*#__PURE__*/_jsx(PageStatusChangeTrackingWrapper, {
                          children: /*#__PURE__*/_jsx(FormRoutingProvider, {
                            children: /*#__PURE__*/_jsx(Router, {
                              history: this.history
                            })
                          })
                        })
                      })]
                    })
                  })]
                })
              })
            })
          })
        })
      })
    }), {
      layoutType: 'sidebar'
    });
  }
}